//
// Copyright (c) 2021 Astute Global, Inc.
//

import AkLinkCommand from "./akLinkCommand";
import { LinkEditing, Plugin } from "ckeditor5";
import {
  ATTRIBUTE_CONTENT_MAPPING,
  ATTRIBUTE_LINK_TOPIC,
  contentMappingModelAttributeToViewAttributes,
  contentMappingViewElementToModelAttribute,
  topicLinkModelAttributeToViewAttributes,
  topicLinkViewElementToModelAttribute,
} from "./util";
import AkUnlinkCommand from "./akUnlinkCommand";

// Use the same priority as regular links
const ATTRIBUTE_PRIORITY = 5;

export default class AkLinkEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [LinkEditing, AkLinkCommand];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "AkLinkEditing";
  }

  init() {
    const editor = this.editor;
    this.linkCommand = new AkLinkCommand(editor, this.editor.commands.get("link"));
    this._setupConfig(editor);
    this._setupSchema(editor);
    this._setupContentMappingConversion(editor);
    this._setupTopicLinkConversion(editor);
    // Replace link and unlink commands with our version
    this.editor.commands.add("link", this.linkCommand);
    // noinspection JSCheckFunctionSignatures
    this.editor.commands.add("unlink", new AkUnlinkCommand(editor));
  }

  _createLinkForContentMapping(modelAttributeValue, writer) {
    const attributes = contentMappingModelAttributeToViewAttributes(modelAttributeValue);
    if (!attributes) {
      return null;
    }
    const element = writer.createAttributeElement("a", attributes, {
      priority: ATTRIBUTE_PRIORITY,
    });
    writer.setCustomProperty("link", true, element);
    return element;
  }

  _createLinkForTopic(modelAttributeValue, writer) {
    const attributes = topicLinkModelAttributeToViewAttributes(modelAttributeValue);
    if (!attributes) {
      return null;
    }
    const element = writer.createAttributeElement("a", attributes, {
      priority: ATTRIBUTE_PRIORITY,
    });
    writer.setCustomProperty("link", true, element);
    return element;
  }

  _setupConfig(editor) {
    editor.config.define("link.contentMappingLink", {
      skip: false,
    });
    editor.config.define("link.topicLink", {
      skip: false,
      unknownLabel: "Unknown Topic",
    });
  }

  _setupContentMappingConversion(editor) {
    const conversion = editor.conversion;
    conversion.for("upcast").elementToAttribute({
      view: {
        name: "a",
        attributes: {
          "data-rdcontent": true,
        },
      },
      model: {
        key: ATTRIBUTE_CONTENT_MAPPING,
        value: (viewElement) => contentMappingViewElementToModelAttribute(editor, viewElement),
      },
    });
    conversion.for("downcast").attributeToElement({
      model: ATTRIBUTE_CONTENT_MAPPING,
      view: (modelAttributeValue, conversionApi, data) => {
        // Manual decorators for block links are handled e.g. in LinkImageEditing.
        if (!(data.item.is("selection") || conversionApi.schema.isInline(data.item))) {
          return;
        }
        return this._createLinkForContentMapping(modelAttributeValue, conversionApi.writer);
      },
    });
  }

  _setupSchema(editor) {
    editor.model.schema.extend("$text", { allowAttributes: ATTRIBUTE_CONTENT_MAPPING });
    editor.model.schema.extend("$text", { allowAttributes: ATTRIBUTE_LINK_TOPIC });
  }

  _setupTopicLinkConversion(editor) {
    const conversion = editor.conversion;
    conversion.for("upcast").elementToAttribute({
      view: {
        name: "a",
        attributes: {
          "data-intent": true,
        },
      },
      model: {
        key: ATTRIBUTE_LINK_TOPIC,
        value: (viewElement) => topicLinkViewElementToModelAttribute(editor, viewElement),
      },
    });
    conversion.for("downcast").attributeToElement({
      model: ATTRIBUTE_LINK_TOPIC,
      view: (modelAttributeValue, conversionApi, data) => {
        // Manual decorators for block links are handled e.g. in LinkImageEditing.
        if (!(data.item.is("selection") || conversionApi.schema.isInline(data.item))) {
          return;
        }
        return this._createLinkForTopic(modelAttributeValue, conversionApi.writer);
      },
    });
  }
}
